/* Phrase Expander — store {trigger -> expansion} pairs that auto-replace
 * when you type the trigger followed by space/tab/enter/punctuation.
 *
 * Example: ";hej" + space  →  "Hej, hoppas du har en bra dag! 💛 "
 *
 * Variables in expansion (replaced at expansion time):
 *   {{date}}      — 2026-05-27
 *   {{time}}      — 14:30
 *   {{datetime}}  — 2026-05-27 14:30
 *   {{day}}       — Onsdag
 *   {{tomorrow}}  — 2026-05-28
 *   {{name}}      — your name (from profile)
 */

export const PHRASES_KEY = 'phrase_snippets_v1';

const DEFAULT_PHRASES = [
  { id: 'p_hej',  trigger: ';hej',  expansion: 'Hej, hoppas du har en bra dag! 💛', enabled: true },
  { id: 'p_tack', trigger: ';tack', expansion: 'Tack så mycket för ditt mejl. Jag återkommer så snart jag kan ✨', enabled: true },
  { id: 'p_sign', trigger: ';sig',  expansion: 'Med vänlig hälsning,\nSofia', enabled: true },
  { id: 'p_dat',  trigger: ';dat',  expansion: '{{date}}', enabled: true },
  { id: 'p_dt',   trigger: ';nu',   expansion: '{{datetime}}', enabled: true },
  { id: 'p_kram', trigger: ';kram', expansion: 'Stor kram 🤗💛', enabled: true },
];

const SWEDISH_DAYS = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];

function pad(n) { return String(n).padStart(2, '0'); }

function formatDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatTime(d) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function expandVariables(text, ctx = {}) {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return (text || '')
    .replace(/\{\{date\}\}/g, formatDate(now))
    .replace(/\{\{time\}\}/g, formatTime(now))
    .replace(/\{\{datetime\}\}/g, `${formatDate(now)} ${formatTime(now)}`)
    .replace(/\{\{day\}\}/g, SWEDISH_DAYS[now.getDay()])
    .replace(/\{\{tomorrow\}\}/g, formatDate(tomorrow))
    .replace(/\{\{name\}\}/g, ctx.name || 'Sofia');
}

export function loadPhrases() {
  try {
    const raw = localStorage.getItem(PHRASES_KEY);
    if (!raw) return [...DEFAULT_PHRASES];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_PHRASES];
    return parsed;
  } catch {
    return [...DEFAULT_PHRASES];
  }
}

export function savePhrases(list) {
  try { localStorage.setItem(PHRASES_KEY, JSON.stringify(list || [])); } catch {/**/}
  try { window.dispatchEvent(new CustomEvent('phrases-changed')); } catch {/**/}
}

export function newPhraseId() {
  return 'p_' + Math.random().toString(36).slice(2, 9);
}

// Find the longest trigger that ends right before `caretText`
// Returns { phrase, matchStart } or null.
export function findMatchingPhrase(textBeforeDelimiter, phrases) {
  let best = null;
  for (const p of phrases) {
    if (!p.enabled) continue;
    if (!p.trigger) continue;
    if (textBeforeDelimiter.endsWith(p.trigger)) {
      // Ensure it's a word-start (preceded by whitespace or beginning of text)
      const startIdx = textBeforeDelimiter.length - p.trigger.length;
      const prevChar = startIdx === 0 ? ' ' : textBeforeDelimiter[startIdx - 1];
      // Allow trigger to start anywhere if it begins with a special char like ; or :
      const startsWithSpecial = /^[^a-zA-Z0-9åäöÅÄÖ]/.test(p.trigger);
      if (startsWithSpecial || /[\s\n\r\t]/.test(prevChar)) {
        if (!best || p.trigger.length > best.phrase.trigger.length) {
          best = { phrase: p, matchStart: startIdx };
        }
      }
    }
  }
  return best;
}
